/**
 * What one checkout submission to `/invoices/action` should do, decided only
 * by the payment method the student picked.
 *
 * Before 2026-10-20 the action redeemed `code` whenever the form carried one.
 * The modal kept a code typed on the "code" tab after the student switched
 * to Vodafone Cash, so the receipt was uploaded and then the stale code was
 * redeemed instead of creating the invoice (no `/invoices/lessons` call).
 */

/** Payment methods whose invoice needs a receipt photo. */
export const RECEIPT_PAYMENT_METHODS = [
  "vodafoneCash",
  "instaPay",
  "bankTransfer",
] as const;

export type InvoiceSubmissionInput = {
  paymentMethod?: string | null;
  code?: string | null;
};

export type InvoiceSubmissionPlan =
  | { kind: "code"; code: string }
  | { kind: "invoice"; requiresReceipt: boolean };

/**
 * Whether this payment method needs a receipt photo.
 *
 * @example
 * paymentMethodRequiresReceipt("vodafoneCash"); // true
 */
export function paymentMethodRequiresReceipt(
  paymentMethod: string | null | undefined,
) {
  return (RECEIPT_PAYMENT_METHODS as readonly string[]).includes(
    paymentMethod ?? "",
  );
}

/**
 * Decide the submission: redeem the code only for the "code" method (or an
 * old client that sent a code without any method); every other method
 * creates an invoice and ignores a leftover code.
 *
 * @example
 * resolveInvoiceSubmission({ paymentMethod: "vodafoneCash", code: "40202162" });
 * // { kind: "invoice", requiresReceipt: true }
 */
export function resolveInvoiceSubmission({
  paymentMethod,
  code,
}: InvoiceSubmissionInput): InvoiceSubmissionPlan {
  const trimmedCode = typeof code === "string" ? code.trim() : "";

  if (paymentMethod === "code" || (!paymentMethod && trimmedCode)) {
    return { kind: "code", code: trimmedCode };
  }

  return {
    kind: "invoice",
    requiresReceipt: paymentMethodRequiresReceipt(paymentMethod),
  };
}
